import {DndContext} from '@dnd-kit/core'
import {restrictToParentElement} from '@dnd-kit/modifiers'
import {Flip} from '@mui/icons-material'
import {Box, Button, Stack, Typography} from '@mui/material'
import {KilnShellPart, MaterialFlowArrow} from '@widgets/kms'
import {Dispatch, SetStateAction, useState} from 'react'

import {useKilnConfig} from '../../providers'
import {KilnConfigActionType, SignalsModalData} from '../../types'

import {Drive} from './Drive'
import {KilnDriveConfigInputs} from './KilnDriveConfig'
import {KilnStationConfigInputs} from './KilnStationConfig'
import {Station} from './Station'

import {KilnShellConfigInputs} from '@kilnfeature/modules/kilnConfig/KilnShellConfig'

interface KilnLayoutProps {
  setSignalsModalData: Dispatch<SetStateAction<SignalsModalData | undefined>>
  disabledSignals: string[]
  setSignalsDisconnectWarning: Dispatch<
    SetStateAction<{
      open: boolean
      signals: string[]
      action: (() => void) | null
    }>
  >
}

export const KilnLayout: React.FC<KilnLayoutProps> = ({
  setSignalsModalData,
  setSignalsDisconnectWarning,
  disabledSignals
}) => {
  const {kilnLayout, kilnConfiguration, dispatch} = useKilnConfig()

  const [selectedKilnPartId, setSelectedKilnPartId] = useState(
    [...kilnLayout.stations, kilnLayout.drive].sort(
      (kilnPartA, kilnPartB) => kilnPartA.x - kilnPartB.x
    )[0].id
  )
  const isKilnDriveSelected = selectedKilnPartId === kilnLayout.drive.id
  const isKilnShellSelected = selectedKilnPartId === kilnLayout.kilnShell.id

  // we need space for station / drive overlays
  const positionYOffset = 85
  return (
    <DndContext
      modifiers={[restrictToParentElement]}
      onDragEnd={(event) => {
        dispatch({
          type: KilnConfigActionType.MOVE_X,
          payload: {id: event.active.id, deltaX: event.delta.x}
        })
      }}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <Stack alignItems={'center'} spacing={2}>
          <Box display="flex">
            <Typography variant="subtitle1" alignSelf="center" color="#52667A">
              Material Flow
            </Typography>
            <Button
              color="primary"
              onClick={() => dispatch({type: KilnConfigActionType.FLIP_MATERIAL_FLOW})}
              startIcon={<Flip />}
              variant="text"
              data-test-id="flip-direction-btn"
            >
              Flip direction
            </Button>
          </Box>
          <MaterialFlowArrow x={0} y={0} materialFlow={kilnLayout.materialFlow} />
        </Stack>
        {/* ---------  KILN LAYOUT --------- */}
        <Box
          sx={{
            width: kilnLayout.referenceDimensions.width,
            height: kilnLayout.interactiveOverlayHeight,
            position: 'relative',
            ml: ({spacing}) => spacing(5),
            mr: ({spacing}) => spacing(12)
          }}
        >
          {/* KILN SHELL */}
          <Box
            sx={{
              position: 'absolute',
              cursor: 'pointer',
              top: kilnLayout.kilnShell.y + positionYOffset,
              left: kilnLayout.kilnShell.x,
              width: kilnLayout.kilnShell.width,
              height: kilnLayout.kilnShell.height
            }}
            data-test-id="kiln-shell-overlay"
            onClick={() => setSelectedKilnPartId(kilnLayout.kilnShell.id)}
          >
            <KilnShellPart
              id={kilnLayout.kilnShell.id}
              x={0}
              y={0}
              height={kilnLayout.kilnShell.height}
              width={kilnLayout.kilnShell.width}
              cones={kilnLayout.kilnShell.cones ?? []}
              kilnDiameter={Number(kilnConfiguration.dimensions.diameter)}
              kilnLength={Number(kilnConfiguration.dimensions.length)}
            />
          </Box>
          {/* STATIONS */}
          {kilnLayout.stations.map((station) => (
            <Station
              key={station.id}
              station={station}
              positionYOffset={positionYOffset}
              isCurrentlySelected={selectedKilnPartId === station.id}
              onClick={() => setSelectedKilnPartId(station.id)}
            />
          ))}
          {/* DRIVE */}
          <Drive
            positionYOffset={positionYOffset}
            isCurrentlySelected={selectedKilnPartId === kilnLayout.drive.id}
            onClick={() => setSelectedKilnPartId(kilnLayout.drive.id)}
          />
        </Box>
        {/* --------- KILN PART DETAILS --------- */}
        {isKilnDriveSelected ? (
          <KilnDriveConfigInputs
            disabledSignals={disabledSignals}
            setSignalsModalData={setSignalsModalData}
            setSignalsDisconnectWarning={setSignalsDisconnectWarning}
          />
        ) : isKilnShellSelected ? (
          <KilnShellConfigInputs />
        ) : (
          <KilnStationConfigInputs
            selectedStationId={selectedKilnPartId}
            disabledSignals={disabledSignals}
            setSignalsModalData={setSignalsModalData}
            setSelectedKilnPartId={setSelectedKilnPartId}
          />
        )}
      </Box>
    </DndContext>
  )
}
